import { useState } from "react";
import { fmt2 } from "@/lib/api";
import { fmtDate } from "@/components/DateInput";
import { fl, srcLabel, trName } from "@/lib/finI18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft } from "lucide-react";
import { useDrill } from "@/components/reports/Drill";
import { Empty } from "@/components/reports/FinViews";

const n = (v) => fmt2(v || 0);
const KINDS = ["vendor", "customer", "employee", "other", "bank"];
const kindLbl = (k, lang) => (KINDS.includes(k) ? fl(`k_${k}`, lang) : "—");
const taxId = (r) => r.npwp || r.nik || "";

function SubList({ report, lang, onPick }) {
  const [moved, setMoved] = useState(false);
  const rows = moved ? report.ledgers.filter((r) => r.count || Math.abs(r.opening) >= 0.005 || Math.abs(r.balance) >= 0.005) : report.ledgers;
  return (
    <Card className="p-4 space-y-3" data-testid="subledger-list">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-muted-foreground">{fl("slHint", lang)} <span className="num" data-testid="subledger-count">({rows.length} {fl("slCount", lang)})</span></p>
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer" data-testid="subledger-moved-toggle">
          <input type="checkbox" checked={moved} onChange={(e) => setMoved(e.target.checked)} className="accent-primary" data-testid="subledger-moved-checkbox" />{fl("slMoved", lang)}
        </label>
      </div>
      <div className="overflow-auto rounded-md border border-border">
        <Table>
          <TableHeader><TableRow>
            <TableHead>{fl("ledger", lang)}</TableHead><TableHead>{fl("kind", lang)}</TableHead><TableHead>{fl("npwpNik", lang)}</TableHead><TableHead>{fl("idTku", lang)}</TableHead>
            <TableHead className="text-right">{fl("opening", lang)}</TableHead><TableHead className="text-right">{fl("debit", lang)}</TableHead>
            <TableHead className="text-right">{fl("credit", lang)}</TableHead><TableHead className="text-right">{fl("balance", lang)}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={8}><Empty lang={lang} /></TableCell></TableRow>}
            {rows.map((r, i) => (
              <TableRow key={r.name} className="cursor-pointer hover:bg-secondary/50" onClick={() => onPick(r.name)} data-testid={`subledger-row-${i}`}>
                <TableCell className="text-sm font-medium text-primary">{trName(r, lang)}</TableCell>
                <TableCell className="text-xs">{kindLbl(r.kind, lang)}</TableCell>
                <TableCell className="text-xs num">{taxId(r) || "—"}</TableCell>
                <TableCell className="text-xs num">{r.id_tku || "—"}</TableCell>
                <TableCell className="num text-right text-xs">{n(r.opening)}</TableCell>
                <TableCell className="num text-right text-xs">{n(r.debit)}</TableCell>
                <TableCell className="num text-right text-xs">{n(r.credit)}</TableCell>
                <TableCell className="num text-right text-xs font-semibold">{n(r.balance)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function SubDetail({ report, lang, onBack }) {
  const drill = useDrill();
  const L = report.ledger;
  return (
    <Card className="overflow-hidden" data-testid="subledger-detail">
      <div className="flex items-center justify-between gap-2 flex-wrap bg-secondary/50 px-4 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="subledger-back"><ArrowLeft className="h-4 w-4 mr-1" />{fl("slBack", lang)}</Button>
          <span className="font-semibold text-sm" data-testid="subledger-name">{trName(L, lang)}</span>
          <span className="text-[11px] text-muted-foreground num">{taxId(L) ? `${L.npwp ? "NPWP" : "NIK"} ${taxId(L)} · ID TKU ${L.id_tku || "—"}` : ""}</span>
        </div>
        <span className="text-xs font-semibold num" data-testid="subledger-balance">{fl("balance", lang)}: {n(report.balance)}</span>
      </div>
      <div className="overflow-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>{fl("date", lang)}</TableHead><TableHead>{fl("docNo", lang)}</TableHead><TableHead>{fl("account", lang)}</TableHead><TableHead>{fl("desc", lang)}</TableHead>
            <TableHead>{fl("source", lang)}</TableHead><TableHead className="text-right">{fl("debit", lang)}</TableHead><TableHead className="text-right">{fl("credit", lang)}</TableHead><TableHead className="text-right">{fl("balance", lang)}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            <TableRow className="bg-secondary/30"><TableCell colSpan={7} className="text-xs italic text-muted-foreground">{fl("opening", lang)}</TableCell><TableCell className="num text-right text-xs font-semibold" data-testid="subledger-opening">{n(report.opening)}</TableCell></TableRow>
            {report.lines.map((l, i) => (
              <TableRow key={i} className="cursor-pointer hover:bg-secondary/50" data-testid={`subledger-line-${i}`} onClick={() => drill && l.doc_id && drill({ doc: l.doc_id, title: `${l.doc_no || srcLabel(l.source, lang)} · ${fmtDate(l.date)}` })}>
                <TableCell className="num text-xs whitespace-nowrap">{fmtDate(l.date)}</TableCell>
                <TableCell className="text-xs">{l.doc_no || "—"}</TableCell>
                <TableCell className="text-xs">{l.account_code} · {l.account_name}</TableCell>
                <TableCell className="text-xs">{l.description}</TableCell>
                <TableCell className="text-[11px] text-muted-foreground">{srcLabel(l.source, lang)}</TableCell>
                <TableCell className="num text-right text-xs">{l.debit ? n(l.debit) : "—"}</TableCell>
                <TableCell className="num text-right text-xs">{l.credit ? n(l.credit) : "—"}</TableCell>
                <TableCell className="num text-right text-xs">{n(l.balance)}</TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-secondary/50 font-semibold">
              <TableCell colSpan={5}>{fl("total", lang)}</TableCell>
              <TableCell className="num text-right" data-testid="subledger-total-debit">{n(report.total_debit)}</TableCell>
              <TableCell className="num text-right" data-testid="subledger-total-credit">{n(report.total_credit)}</TableCell>
              <TableCell className="num text-right">{n(report.balance)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

export function SubledgerView({ report, lang, onPick, onBack }) {
  return report.ledgers ? <SubList report={report} lang={lang} onPick={onPick} /> : <SubDetail report={report} lang={lang} onBack={onBack} />;
}
